import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
  Users,
  DollarSign,
  Activity,
  TrendingUp,
  Calendar,
  BarChart3,
  Target,
} from 'lucide-react';

// ── Reusable Stat Card ───────────────────────────────────────────────────────
const StatCard = ({ title, value, color, trend }) => (
  <div className={`bg-white p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-sm border border-gray-100 border-l-4 ${color.replace('bg-', 'border-l-')}`}>
    <p className="text-xs sm:text-sm font-medium text-gray-500 mb-1 truncate">{title}</p>
    <h3 className="text-xl sm:text-3xl font-bold text-gray-900 tracking-tight">{value}</h3>
    {trend && <p className="text-[10px] sm:text-xs font-medium text-green-600 mt-1 sm:mt-2 flex items-center gap-1 truncate">{trend}</p>}
  </div>
);

// ── Monthly Insights Component ───────────────────────────────────────────────
const MonthlyInsights = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get('/admin/dashboard-analytics/?period=month');
        setData(res.data);
      } catch (error) {
        console.error('Monthly insights fetch error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;

  const summary = data?.summary || {};
  const byMethod = data?.by_method || {};
  const byOfficer = data?.by_officer || [];
  const dailyBreakdown = data?.daily_breakdown || [];
  const yearlySnapshot = data?.yearly_snapshot || [];

  const monthName = data?.month
    ? new Date(data.month + '-01').toLocaleString('default', { month: 'long', year: 'numeric' })
    : '';

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">

      {/* Stat Cards */}
      <div className="mb-6 sm:mb-10">
        <h2 className="text-base sm:text-lg font-bold text-gray-800 mb-4 sm:mb-6 flex items-center gap-2">
          <span className="w-1 h-6 bg-blue-600 rounded-full"></span> {monthName} Overview
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          <StatCard title="Monthly Collected" value={`PKR ${summary.total_collected?.toLocaleString() || 0}`} color="bg-emerald-500" trend={`${summary.collection_count || 0} entries`} />
          <StatCard title="Expected Revenue" value={`PKR ${Math.round(summary.expected_monthly_revenue || 0).toLocaleString()}`} color="bg-amber-500" trend={`${summary.active_customers || 0} active customers`} />
          <StatCard title="Collection Rate" value={`${summary.collection_rate || 0}%`} color={summary.collection_rate >= 80 ? 'bg-green-500' : summary.collection_rate >= 50 ? 'bg-amber-500' : 'bg-red-500'} trend={summary.collection_rate >= 80 ? 'On track' : 'Below target'} />
          <StatCard title="Active Customers" value={summary.active_customers || 0} color="bg-indigo-500" />
        </div>
      </div>

      {/* Payment Method Breakdown */}
      <div className="mb-6 sm:mb-8">
        <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
          <span className="w-1 h-4 bg-purple-500 rounded-full"></span> Collections by Method
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
            <p className="text-xs font-medium text-gray-500 mb-1">Cash</p>
            <p className="text-lg sm:text-xl font-bold text-green-700">PKR {Math.round(byMethod.cash || 0).toLocaleString()}</p>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
            <p className="text-xs font-medium text-gray-500 mb-1">JazzCash</p>
            <p className="text-lg sm:text-xl font-bold text-red-700">PKR {Math.round(byMethod.jazzcash || 0).toLocaleString()}</p>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
            <p className="text-xs font-medium text-gray-500 mb-1">EasyPaisa</p>
            <p className="text-lg sm:text-xl font-bold text-blue-700">PKR {Math.round(byMethod.easypaisa || 0).toLocaleString()}</p>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
            <p className="text-xs font-medium text-gray-500 mb-1">Bank</p>
            <p className="text-lg sm:text-xl font-bold text-indigo-700">PKR {Math.round(byMethod.bank || 0).toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Officer Performance */}
      {byOfficer.length > 0 && (
        <div className="mb-6 sm:mb-8">
          <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
            <span className="w-1 h-4 bg-blue-500 rounded-full"></span> Officer Performance (This Month)
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {byOfficer.map((o) => {
              const rate = summary.expected_monthly_revenue > 0
                ? Math.round((o.collected / summary.expected_monthly_revenue) * 100)
                : 0;
              return (
                <div key={o.officer_id} className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-semibold text-gray-900 text-sm">{o.officer_name}</p>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{o.count} entries</span>
                  </div>
                  <p className="text-lg font-bold text-emerald-700">PKR {Math.round(o.collected).toLocaleString()}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Daily Breakdown Table */}
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-6 sm:mb-8">
        <div className="p-4 sm:p-6 border-b border-gray-100 bg-gray-50/50 dark:bg-gray-800/50 dark:border-gray-700">
          <h3 className="font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2 text-sm sm:text-base">
            <Calendar size={18} className="text-gray-400" /> Daily Breakdown ({dailyBreakdown.length} days)
          </h3>
        </div>

        {/* Desktop Table */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase font-semibold">
              <tr>
                <th className="p-4">Date</th>
                <th className="p-4 text-right">Cash</th>
                <th className="p-4 text-right">JazzCash</th>
                <th className="p-4 text-right">EasyPaisa</th>
                <th className="p-4 text-right">Bank</th>
                <th className="p-4 text-right">Total</th>
                <th className="p-4 text-right">Entries</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {dailyBreakdown.length === 0 ? (
                <tr><td colSpan="7" className="p-8 text-center text-gray-500">No collections this month.</td></tr>
              ) : (
                dailyBreakdown.map((d) => (
                  <tr key={d.date} className="hover:bg-blue-50/30 transition-colors">
                    <td className="p-4 font-medium text-gray-900">
                      {new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' })}
                    </td>
                    <td className="p-4 text-right text-sm text-green-700">{d.by_method.cash > 0 ? `PKR ${Math.round(d.by_method.cash).toLocaleString()}` : '-'}</td>
                    <td className="p-4 text-right text-sm text-red-700">{d.by_method.jazzcash > 0 ? `PKR ${Math.round(d.by_method.jazzcash).toLocaleString()}` : '-'}</td>
                    <td className="p-4 text-right text-sm text-blue-700">{d.by_method.easypaisa > 0 ? `PKR ${Math.round(d.by_method.easypaisa).toLocaleString()}` : '-'}</td>
                    <td className="p-4 text-right text-sm text-indigo-700">{d.by_method.bank > 0 ? `PKR ${Math.round(d.by_method.bank).toLocaleString()}` : '-'}</td>
                    <td className="p-4 text-right font-bold text-gray-900">PKR {Math.round(d.total).toLocaleString()}</td>
                    <td className="p-4 text-right text-sm text-gray-500">{d.count}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="sm:hidden divide-y divide-gray-100">
          {dailyBreakdown.length === 0 ? (
            <div className="p-6 text-center text-gray-500 text-sm">No collections this month.</div>
          ) : (
            dailyBreakdown.map((d) => (
              <div key={d.date} className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-semibold text-gray-900 text-sm">
                    {new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' })}
                  </p>
                  <span className="font-bold text-gray-900 text-sm">PKR {Math.round(d.total).toLocaleString()}</span>
                </div>
                <div className="flex gap-3 text-xs text-gray-500">
                  {d.by_method.cash > 0 && <span className="text-green-600">Cash: {Math.round(d.by_method.cash).toLocaleString()}</span>}
                  {d.by_method.jazzcash > 0 && <span className="text-red-600">JC: {Math.round(d.by_method.jazzcash).toLocaleString()}</span>}
                  {d.by_method.easypaisa > 0 && <span className="text-blue-600">EP: {Math.round(d.by_method.easypaisa).toLocaleString()}</span>}
                  {d.by_method.bank > 0 && <span className="text-indigo-600">Bank: {Math.round(d.by_method.bank).toLocaleString()}</span>}
                  <span className="ml-auto">{d.count} entries</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Yearly Snapshot */}
      {yearlySnapshot.length > 0 && (
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 sm:p-6 border-b border-gray-100 bg-gray-50/50 dark:bg-gray-800/50 dark:border-gray-700">
            <h3 className="font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2 text-sm sm:text-base">
              <BarChart3 size={18} className="text-gray-400" /> {new Date().getFullYear()} Performance
            </h3>
          </div>

          {/* Desktop Table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase font-semibold">
                <tr>
                  <th className="p-4">Month</th>
                  <th className="p-4 text-right">Total Collected</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {yearlySnapshot.map((y) => (
                  <tr key={y.month} className="hover:bg-blue-50/30 transition-colors">
                    <td className="p-4 font-medium text-gray-900">
                      {new Date(y.month + '-01').toLocaleString('default', { month: 'long' })}
                    </td>
                    <td className="p-4 text-right font-bold text-gray-900">PKR {Math.round(y.total_collected).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="sm:hidden divide-y divide-gray-100">
            {yearlySnapshot.map((y) => (
              <div key={y.month} className="p-4 flex items-center justify-between">
                <p className="font-medium text-gray-900 text-sm">
                  {new Date(y.month + '-01').toLocaleString('default', { month: 'long' })}
                </p>
                <span className="font-bold text-gray-900 text-sm">PKR {Math.round(y.total_collected).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthlyInsights;
